import { useEffect, useState } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import { brutoCapex, capexLlevaIvaLocal } from "../../utils/brutoCapex";
import { getCuotasDeCosto, guardarCostoProyecto } from "../../utils/costosProyecto";
import { generarCuotas, validarSumaCuotas } from "../../utils/cuotasUtil";
import { hoyDelPais } from "../../utils/fechasFuturosPanel";
import { useCurrency } from "../../utils/useCurrency";
import { exigirFechaValida, exigirFechaValidaEnLista, MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// Plan de cuotas de un costo de proyecto de Cartelería (menú ⋮ de CarteleriaCostosModal).
//
// Mirror DELIBERADO de `FlujoCajaV2/PlanPagosActivoModal.js` en el reparto bruto→neto (10.213:
// reparto bruto en pantalla, guardado en neto, última cuota absorbe el redondeo) — reusa las MISMAS
// piezas puras y entity-agnostic (`generarCuotas`/`validarSumaCuotas` de cuotasUtil.js,
// `brutoCapex`/`capexLlevaIvaLocal` de brutoCapex.js), pero NO se extrajo código de ese modal: el
// modelo de estados es distinto. `costos_proyecto` en esta fase no tiene ledger de pagos (no existe
// "cuota pagada", no hay "virgen" que proteger) — todas las cuotas son siempre editables y solo hay
// 2 estados ('plan' | 'fecha_unica'; 'cero' existe en la RPC pero se rechaza para un costo vigente,
// D-2 de la Fase 1). Extraer la lógica de "pagadas" de PlanPagosActivoModal habría sido más riesgoso
// (tocar Inversión) que escribir estas ~15 líneas de reparto proporcional de nuevo sobre los mismos
// primitivos compartidos — ver docs/decisiones/2026-10-03-costos-carteleria-fase2.md.
const PlanCuotasCostoModal = ({ show, costo, pais, userId, onHide, onSaved }) => {
  const [cargando, setCargando] = useState(true);
  const [cuotas, setCuotas] = useState([]);
  const [numCuotas, setNumCuotas] = useState(4);
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [fechaInicio, setFechaInicio] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [modo, setModo] = useState("plan"); // 'plan' | 'fecha_unica'
  const [fechaUnica, setFechaUnica] = useState("");

  const { formatAmount } = useCurrency();

  const totalNeto = Number(costo?.monto || 0);
  const llevaIva = capexLlevaIvaLocal(costo?.moneda, pais, costo?.exento_iva);
  const total = brutoCapex(totalNeto, { exento_iva: costo?.exento_iva, moneda: costo?.moneda, pais });

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const plan = await getCuotasDeCosto(costo.id);
        if (!vivo) return;
        const aBrutoCuota = (neto) =>
          brutoCapex(Number(neto || 0), { exento_iva: costo.exento_iva, moneda: costo.moneda, pais });
        const normalizadas = plan
          .map((c) => ({ ...c, _dbNet: Number(c.monto_cuota), monto_cuota: aBrutoCuota(c.monto_cuota) }))
          .sort(
            (a, b) =>
              String(a.fecha_vencimiento).localeCompare(String(b.fecha_vencimiento)) ||
              (Number(a.numero_cuota) || 0) - (Number(b.numero_cuota) || 0)
          );
        setCuotas(normalizadas);
        setModo(normalizadas.length > 0 ? "plan" : "fecha_unica");
        const hoyPais = hoyDelPais(pais);
        setFechaInicio(hoyPais);
        setFechaUnica(costo.fecha_prevista || hoyPais);
      } catch (e) {
        toast.error(`Error al cargar el plan: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costo.id]);

  const sumaCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const cuadra = validarSumaCuotas(cuotas, total);
  const puedeGuardar =
    !cargando &&
    !guardando &&
    ((modo === "plan" && cuotas.length > 0 && cuadra) || (modo === "fecha_unica" && !!fechaUnica));

  const handleGenerar = () => {
    const n = Math.trunc(Number(numCuotas));
    if (!Number.isFinite(n) || n < 1) return toast.error("Ingresá al menos 1 cuota");
    if (!fechaInicio) return toast.error("Elegí una fecha de inicio");
    const nuevas = generarCuotas(total, n, fechaInicio, frecuencia, true);
    setCuotas(nuevas);
  };

  const handleVaciar = () => setCuotas([]);

  const handleCambiarCuota = (idx, field, value) => {
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleAgregarFila = () => {
    const hoyPais = hoyDelPais(pais);
    setCuotas((prev) => [...prev, { numero_cuota: prev.length + 1, monto_cuota: 0, fecha_vencimiento: hoyPais }]);
  };

  const handleQuitarFila = (idx) => setCuotas((prev) => prev.filter((_, i) => i !== idx));

  // Bruto (pantalla) → neto (DB): reparto proporcional al bruto de cada cuota sobre el neto total;
  // la ÚLTIMA cuota absorbe el residuo (10.213) → Σ(neto) == costo.monto EXACTO. Sin ledger de pagos
  // en esta fase: TODAS las cuotas entran al reparto (a diferencia de PlanPagosActivoModal, que
  // excluye las pagadas).
  const construirCuotasNeto = () => {
    const brutos = cuotas.map((c) => Number(c.monto_cuota || 0));
    const sumaBruto = brutos.reduce((s, b) => s + b, 0) || 1;
    let acumNeto = 0;
    return cuotas.map((c, i) => {
      const esUltima = i === cuotas.length - 1;
      const net = esUltima ? totalNeto - acumNeto : Math.round((totalNeto * brutos[i]) / sumaBruto);
      if (!esUltima) acumNeto += net;
      return { ...(c.id ? { id: c.id } : {}), monto_cuota: net, fecha_vencimiento: c.fecha_vencimiento };
    });
  };

  const handleGuardar = async () => {
    if (modo === "plan") {
      if (cuotas.length === 0) return toast.error("Agregá al menos una cuota");
      if (!cuadra)
        return toast.error(
          `Las cuotas (${formatAmount(sumaCuotas, costo.moneda)}) no suman el total (${formatAmount(total, costo.moneda)})`
        );
    }
    if (modo === "fecha_unica" && !fechaUnica) return toast.error("Elegí la fecha del pago único");

    setGuardando(true);
    try {
      const base = {
        costoId: costo.id,
        proveedorId: costo.proveedor_id,
        concepto: costo.concepto,
        descripcion: costo.descripcion || "",
        monto: Number(costo.monto),
        moneda: costo.moneda,
        exentoIva: !!costo.exento_iva,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
      };
      if (modo === "plan") {
        const cuotasNeto = construirCuotasNeto();
        exigirFechaValidaEnLista(cuotasNeto, "fecha_vencimiento", { modo: MODOS_VALIDACION.DATO_DOCUMENTO });
        await guardarCostoProyecto({ ...base, estado: "plan", cuotas: cuotasNeto });
      } else {
        exigirFechaValida({ fechaUnica }, "fechaUnica", { modo: MODOS_VALIDACION.DATO_DOCUMENTO });
        await guardarCostoProyecto({ ...base, estado: "fecha_unica", fechaUnica });
      }
      toast.success("Plan de cuotas guardado");
      onSaved?.(costo.id);
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Plan de cuotas del costo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 12 }}>
          <div>
            <strong>Concepto:</strong> {costo?.descripcion || costo?.concepto}
          </div>
          <div>
            <strong>Monto total:</strong>{" "}
            {cargando ? "…" : `${formatAmount(total, costo.moneda)}${llevaIva ? " (con IVA)" : ""}`}
          </div>
        </div>

        {/* Selector de estado: solo 'plan' | 'fecha_unica' — 'cero' no es válido para un costo
            vigente en esta fase (D-2, migración 20261003f). */}
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {[
            { key: "plan", label: "Plan de cuotas" },
            { key: "fecha_unica", label: "Fecha única" },
          ].map((op) => (
            <Button
              key={op.key}
              size="sm"
              variant={modo === op.key ? "primary" : "outline-secondary"}
              onClick={() => setModo(op.key)}
              disabled={cargando || guardando}
            >
              {op.label}
            </Button>
          ))}
        </div>

        {modo === "fecha_unica" && (
          <div style={{ padding: "10px 12px", background: "#f8f9fa", borderRadius: 6, marginBottom: 12 }}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Fecha del pago único</Form.Label>
              <SelectorFechaDocumento
                value={fechaUnica}
                onChange={(nuevaFecha) => setFechaUnica(nuevaFecha || "")}
                pais={pais}
                modo={MODOS_VALIDACION.DATO_DOCUMENTO}
                disabled={guardando}
                className="form-control"
                style={{ width: 180 }}
              />
            </Form.Group>
            <div style={{ color: "#888", fontSize: "0.8rem", marginTop: 6 }}>
              Proyecta UNA línea por el monto total ({formatAmount(total, costo.moneda)}) en esa fecha.
            </div>
          </div>
        )}

        {modo === "plan" && (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                flexWrap: "wrap",
                padding: "10px 12px",
                background: "#f8f9fa",
                borderRadius: 6,
                marginBottom: 12,
              }}
            >
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>N° cuotas</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={numCuotas}
                  onChange={(e) => setNumCuotas(e.target.value)}
                  disabled={guardando}
                  style={{ width: 90 }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Frecuencia</Form.Label>
                <Form.Select
                  value={frecuencia}
                  onChange={(e) => setFrecuencia(e.target.value)}
                  disabled={guardando}
                  style={{ width: 130 }}
                >
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Fecha inicio</Form.Label>
                <SelectorFechaDocumento
                  value={fechaInicio}
                  onChange={(nuevaFecha) => setFechaInicio(nuevaFecha || "")}
                  pais={pais}
                  modo={MODOS_VALIDACION.DATO_DOCUMENTO}
                  disabled={guardando}
                  className="form-control"
                  style={{ width: 160 }}
                />
              </Form.Group>
              <Button variant="outline-secondary" size="sm" onClick={handleGenerar} disabled={cargando || guardando}>
                Generar cuotas
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleVaciar}
                disabled={cargando || guardando || cuotas.length === 0}
              >
                Vaciar
              </Button>
            </div>

            <Table size="sm" bordered style={{ fontSize: "0.85rem", marginBottom: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Vencimiento</th>
                  <th>Monto ({costo.moneda})</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {cuotas.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#888" }}>
                      Sin cuotas. Generá o agregá filas.
                    </td>
                  </tr>
                ) : (
                  cuotas.map((c, idx) => (
                    <tr key={c.id || `nueva-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>
                        <SelectorFechaDocumento
                          value={String(c.fecha_vencimiento || "").substring(0, 10)}
                          onChange={(nuevaFecha) => handleCambiarCuota(idx, "fecha_vencimiento", nuevaFecha || "")}
                          pais={pais}
                          modo={MODOS_VALIDACION.DATO_DOCUMENTO}
                          disabled={guardando}
                          className="form-control form-control-sm"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={c.monto_cuota}
                          onChange={(e) => handleCambiarCuota(idx, "monto_cuota", e.target.value)}
                          disabled={guardando}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          variant="link"
                          size="sm"
                          style={{ color: "#c62828", padding: 0, textDecoration: "none" }}
                          onClick={() => handleQuitarFila(idx)}
                          disabled={guardando}
                        >
                          ✕
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleAgregarFila}
                disabled={cargando || guardando}
              >
                + Agregar cuota
              </Button>
              <div style={{ fontSize: "0.9rem", color: cuadra ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                Σ cuotas: {formatAmount(sumaCuotas, costo.moneda)} / {formatAmount(total, costo.moneda)}
                {!cuadra && <span style={{ marginLeft: 8, fontWeight: 400 }}>(no cuadra)</span>}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={!puedeGuardar}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanCuotasCostoModal;
